import API from '../api';
import * as actions from '../constants';

/**
 * Action to set a user error
 */
export const showError = (error): any => ({
  error,
  type: actions.SHOW_USER_ERROR,
});

/**
 * Action to clear user errors
 */
export const clearErrors = (): any => ({
  type: actions.CLEAR_USER_ERRORS,
});

/**
 * Action to mark a fetch users request as sent
 */
export const requestUsers = (): any => ({
  type: actions.REQUEST_USERS,
});

/**
 * Action to mark a fetch users request as received
 * @param {String|Boolean} error
 * @param {Object} data
 */
export const receiveUsers = (error, data, date = new Date()): any => ({
  error,
  data,
  date,
  type: actions.RECEIVE_USERS,
});

/**
 * Fetch users
 * @return {Promise}
 */
export const getUsers = (date = new Date()): any => (dispatch) => {
  dispatch(requestUsers());
  return API.request('/users')
    .then((response) => response.json())
    .then((response) => {
      dispatch(receiveUsers(response.error, response.data, date));
      return response;
    });
};

/**
 * Action to mark a fetch user request as sent
 * @param {Number} id
 */
export const requestUser = (id): any => ({
  id,
  type: actions.REQUEST_USER,
});

/**
 * Action to mark a fetch user request as received
 * @param {Number} id
 * @param {String|Boolean} error
 * @param {Object} data
 */
export const receiveUser = (id, error, data, date = new Date()): any => ({
  id,
  error,
  data,
  date,
  type: actions.RECEIVE_USER,
});

/**
 * Fetch a user
 * @param {Number} id
 * @return {Promise}
 */
export const getUser = (id, date = new Date()): any => (dispatch) => {
  dispatch(requestUser(id));
  return API.request(`/users/${id}`)
    .then((response) => response.json())
    .then((response) => {
      dispatch(receiveUser(id, response.error, response.data, date));
      return response;
    });
};

/**
 * Action to mark an add user request as sent
 */
export const requestAddUser = (): any => ({
  type: actions.REQUEST_ADD_USER,
});

/**
 * Action to mark an add user request as received
 * @param {String} email
 * @param {String|Boolean} error
 * @param {Object} data
 */
export const receiveAddUser = (error, data, date = new Date()): any => ({
  error,
  data,
  date,
  type: actions.RECEIVE_ADD_USER,
});

/**
 * Add a user
 * @param {Object} fields
 * @return {Promise}
 */
export const addUser = (fields, date = new Date()): any => (dispatch) => {
  dispatch(requestAddUser());
  return API.request('/users', 'POST', { email: fields.email })
    .then((response) => response.json())
    .then((response) => {
      dispatch(receiveAddUser(response.error, response.data, date));
      return response;
    });
};

/**
 * Action to mark a fetch user permission request as sent
 * @param {Number} id
 */
export const requestUserPermissions = (id): any => ({
  id,
  type: actions.REQUEST_USER_PERMISSIONS,
});

/**
 * Action to mark a fetch user permissions request as received
 * @param {Number} id
 * @param {String|Boolean} error
 * @param {Object} data
 */
export const receiveUserPermissions = (
  id,
  error,
  data,
  date = new Date(),
): any => ({
  id,
  error,
  data,
  date,
  type: actions.RECEIVE_USER_PERMISSIONS,
});

/**
 * Fetch a user's permissions
 * @param {Number} id
 * @return {Promise}
 */
export const getUserPermissions = (id, date = new Date()): any => (
  dispatch,
) => {
  dispatch(requestUserPermissions(id));
  return API.request(`/users/${id}/permissions`)
    .then((response) => response.json())
    .then((response) => {
      dispatch(receiveUserPermissions(id, response.error, response.data, date));
      return response;
    });
};

/**
 * Action to mark a fetch user accounts request as sent
 * @param {Number} id
 */
export const requestUserAccounts = (id): any => ({
  id,
  type: actions.REQUEST_USER_ACCOUNTS,
});

/**
 * Action to mark a fetch user accounts request as received
 * @param {Number} id
 * @param {String|Boolean} error
 * @param {Object} data
 */
export const receiveUserAccounts = (
  id,
  error,
  data,
  date = new Date(),
): any => ({
  id,
  error,
  data,
  date,
  type: actions.RECEIVE_USER_ACCOUNTS,
});

/**
 * Fetch a user's accounts
 * @param {Number} id
 * @return {Promise}
 */
export const getUserAccounts = (id, date = new Date()): any => (dispatch) => {
  dispatch(requestUserAccounts(id));
  return API.request(`/users/${id}/accounts`)
    .then((response) => response.json())
    .then((response) => {
      dispatch(receiveUserAccounts(id, response.error, response.data, date));
      return response;
    });
};

/**
 * Action to mark an add permission to user request as sent
 * @param {Number} id
 * @param {String} permission
 */
export const requestAddPermissionToUser = (id, permission): any => ({
  id,
  permission,
  type: actions.REQUEST_ADD_PERMISSION_TO_USER,
});

/**
 * Action to mark an add permission to user request as received
 * @param {Number} id
 * @param {String} permission
 * @param {String|Boolean} error
 * @param {Object} data
 */
export const receiveAddPermissionToUser = (
  id,
  permission,
  error,
  data,
  date = new Date(),
): any => ({
  id,
  permission,
  error,
  data,
  date,
  type: actions.RECEIVE_ADD_PERMISSION_TO_USER,
});

/**
 * Add a permission to a user
 * @param {Number} id
 * @param {String} permission
 * @return {Promise}
 */
export const addPermissionToUser = (id, permission, date = new Date()): any => (
  dispatch,
) => {
  dispatch(requestAddPermissionToUser(id, permission));
  return API.request(`/users/${id}/permissions`, 'POST', { permission })
    .then((response) => response.json())
    .then((response) => {
      dispatch(
        receiveAddPermissionToUser(
          id,
          permission,
          response.error,
          response.data,
          date,
        ),
      );
      return response;
    });
};

/**
 * Action to mark a remove permission from user request as sent
 * @param {Number} id
 * @param {String} permission
 */
export const requestRemovePermissionFromUser = (id, permission): any => ({
  id,
  permission,
  type: actions.REQUEST_REMOVE_PERMISSION_FROM_USER,
});

/**
 * Action to mark a remove permission from user request as received
 * @param {Number} id
 * @param {String} permission
 * @param {String|Boolean} error
 * @param {Object} data
 */
export const receiveRemovePermissionFromUser = (
  id,
  permission,
  error,
  data,
  date = new Date(),
): any => ({
  id,
  permission,
  error,
  data,
  date,
  type: actions.RECEIVE_REMOVE_PERMISSION_FROM_USER,
});

/**
 * Remove a permission from a user
 * @param {Number} id
 * @param {String} permission
 * @return {Promise}
 */
export const removePermissionFromUser = (
  id,
  permission,
  date = new Date(),
): any => (dispatch) => {
  dispatch(requestRemovePermissionFromUser(id, permission));
  return API.request(`/users/${id}/permissions`, 'DELETE', { permission })
    .then((response) => response.json())
    .then((response) => {
      dispatch(
        receiveRemovePermissionFromUser(
          id,
          permission,
          response.error,
          response.data,
          date,
        ),
      );
      return response;
    });
};

/**
 * Action to mark an add account to user request as sent
 * @param {Number} id
 * @param {Number} accountId
 */
export const requestAddDefaultAccountToUser = (id, accountId): any => ({
  id,
  accountId,
  type: actions.REQUEST_ADD_DEFAULT_ACCOUNT_TO_USER,
});

/**
 * Action to mark an add account to user request as received
 * @param {Number} id
 * @param {Number} accountId
 * @param {String|Boolean} error
 * @param {Object} data
 */
export const receiveAddDefaultAccountToUser = (
  id,
  accountId,
  error,
  data,
  date = new Date(),
): any => ({
  id,
  accountId,
  error,
  data,
  date,
  type: actions.RECEIVE_ADD_DEFAULT_ACCOUNT_TO_USER,
});

/**
 * Add a site to a user
 * @param {Number} id
 * @param {Number} accountId
 * @return {Promise}
 */
export const addDefaultAccountToUser = (
  id,
  accountId,
  date = new Date(),
): any => (dispatch) => {
  dispatch(requestAddDefaultAccountToUser(id, accountId));
  return API.request(`/users/${id}/accounts/default`, 'POST', {
    account_id: accountId,
  })
    .then((response) => response.json())
    .then((response) => {
      dispatch(
        receiveAddDefaultAccountToUser(
          id,
          accountId,
          response.error,
          response.data,
          date,
        ),
      );
      return response;
    });
};

/**
 * Action to mark an add account to user request as sent
 * @param {Number} id
 * @param {Number} accountId
 */
export const requestAddAccountToUser = (id, accountId): any => ({
  id,
  accountId,
  type: actions.REQUEST_ADD_ACCOUNT_TO_USER,
});

/**
 * Action to mark an add account to user request as received
 * @param {Number} id
 * @param {Number} accountId
 * @param {String|Boolean} error
 * @param {Object} data
 */
export const receiveAddAccountToUser = (
  id,
  accountId,
  error,
  data,
  date = new Date(),
): any => ({
  id,
  accountId,
  error,
  data,
  date,
  type: actions.RECEIVE_ADD_ACCOUNT_TO_USER,
});

/**
 * Add a site to a user
 * @param {Number} id
 * @param {Number} accountId
 * @return {Promise}
 */
export const addAccountToUser = (id, accountId, date = new Date()): any => (
  dispatch,
) => {
  dispatch(requestAddAccountToUser(id, accountId));
  return API.request(`/users/${id}/accounts`, 'POST', { account_id: accountId })
    .then((response) => response.json())
    .then((response) => {
      dispatch(
        receiveAddAccountToUser(
          id,
          accountId,
          response.error,
          response.data,
          date,
        ),
      );
      return response;
    });
};

/**
 * Action to mark a remove account from user request as sent
 * @param {Number} id
 * @param {Number} accountId
 */
export const requestRemoveAccountFromUser = (id, accountId): any => ({
  id,
  accountId,
  type: actions.REQUEST_REMOVE_ACCOUNT_FROM_USER,
});

/**
 * Action to mark a remove account from user request as received
 * @param {Number} id
 * @param {Number} accountId
 * @param {String|Boolean} error
 * @param {Object} data
 */
export const receiveRemoveAccountFromUser = (
  id,
  accountId,
  error,
  data,
  date = new Date(),
): any => ({
  id,
  accountId,
  error,
  data,
  date,
  type: actions.RECEIVE_REMOVE_ACCOUNT_FROM_USER,
});

/**
 * Remove an account from a user
 * @param {Number} id
 * @param {Number} accountId
 * @return {Promise}
 */
export const removeAccountFromUser = (
  id,
  accountId,
  date = new Date(),
): any => (dispatch) => {
  dispatch(requestRemoveAccountFromUser(id, accountId));
  return API.request(`/users/${id}/accounts`, 'DELETE', {
    account_id: accountId,
  })
    .then((response) => response.json())
    .then((response) => {
      dispatch(
        receiveRemoveAccountFromUser(
          id,
          accountId,
          response.error,
          response.data,
          date,
        ),
      );
      return response;
    });
};

/**
 * Action to mark a remove a user request as sent
 * @param {Number} id
 */
export const requestRemoveUser = (id): any => ({
  id,
  type: actions.REQUEST_REMOVE_USER,
});

/**
 * Action to mark a remove a user request as received
 * @param {Number} id
 * @param {String|Boolean} error
 * @param {Object} data
 */
export const receiveRemoveUser = (id, error, data, date = new Date()): any => ({
  id,
  error,
  data,
  date,
  type: actions.RECEIVE_REMOVE_USER,
});

/**
 * Remove a user
 * @param {Number} id
 * @param {Number} userId
 * @return {Promise}
 */
export const removeUser = (id, date = new Date()): any => (dispatch) => {
  dispatch(requestRemoveUser(id));
  return API.request(`/users/${id}`, 'DELETE')
    .then((response) => response.json())
    .then((response) => {
      dispatch(receiveRemoveUser(id, response.error, response.data, date));
      return response;
    });
};
